import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import Header from "./Header";
import Footer from "./Footer";
import { BrandMark } from "../branding/BrandText";
import "../assets/stylesheets/CaliforniaHiddenTaxFeeTransparencyPetition.css";
import { useBrand } from "../branding/BrandContext";

const PAGE_DESCRIPTION =
  "Support a California taxpayer petition requiring voter approval, public cost studies, and plain-language disclosure for government fees, rates, cost recoveries, assessments, and other revenue increases that function like taxes.";
const OG_TITLE = "If It Raises Revenue, Californians Should Get to Vote";
const OG_DESCRIPTION =
  "A California taxpayer transparency petition for voter approval and public cost studies before governments increase revenue through fees, rates, cost recoveries, assessments, surcharges, or taxes by another name.";

function setOrCreateMeta(selector, attributes) {
  const existing = document.head.querySelector(selector);
  const previous = existing
    ? Object.fromEntries(Object.keys(attributes).map((key) => [key, existing.getAttribute(key)]))
    : null;

  const element = existing || document.createElement("meta");
  Object.entries(attributes).forEach(([key, value]) => {
    if (value == null) {
      element.removeAttribute(key);
    } else {
      element.setAttribute(key, value);
    }
  });

  if (!existing) {
    document.head.appendChild(element);
  }

  return () => {
    if (previous) {
      Object.entries(previous).forEach(([key, value]) => {
        if (value == null) {
          element.removeAttribute(key);
        } else {
          element.setAttribute(key, value);
        }
      });
      return;
    }

    element.remove();
  };
}

function setOrCreateLink(selector, attributes) {
  const existing = document.head.querySelector(selector);
  const previous = existing
    ? Object.fromEntries(Object.keys(attributes).map((key) => [key, existing.getAttribute(key)]))
    : null;

  const element = existing || document.createElement("link");
  Object.entries(attributes).forEach(([key, value]) => {
    if (value == null) {
      element.removeAttribute(key);
    } else {
      element.setAttribute(key, value);
    }
  });

  if (!existing) {
    document.head.appendChild(element);
  }

  return () => {
    if (previous) {
      Object.entries(previous).forEach(([key, value]) => {
        if (value == null) {
          element.removeAttribute(key);
        } else {
          element.setAttribute(key, value);
        }
      });
      return;
    }

    element.remove();
  };
}

export default function CaliforniaHiddenTaxFeeTransparencyPetition() {
  const brand = useBrand();
  const petitionRef = useRef(null);
  const [formStatus, setFormStatus] = useState("");
  const [clipboardStatus, setClipboardStatus] = useState("");

  const pageUrl = `${brand.consumerBaseUrl}/california-hidden-tax-fee-transparency-petition`;
  const pageTitle = `California Hidden Tax & Fee Transparency Petition | ${brand.brandName}`;
  const sharedImage = `${brand.consumerBaseUrl}/assets/${brand.assetNamespace}-mascots.png`;

  const structuredData = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: pageTitle,
      description: PAGE_DESCRIPTION,
      url: pageUrl,
      publisher: {
        "@type": "Organization",
        name: brand.brandName,
        url: brand.consumerBaseUrl,
      },
    }),
    [brand, pageTitle, pageUrl]
  );

  useLayoutEffect(() => {
    window.scrollTo(0, 0);

    const previousTitle = document.title;
    document.title = pageTitle;

    const cleanups = [
      setOrCreateMeta('meta[name="description"]', { name: "description", content: PAGE_DESCRIPTION }),
      setOrCreateMeta('meta[name="robots"]', {
        name: "robots",
        content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
      }),
      setOrCreateMeta('meta[property="og:type"]', { property: "og:type", content: "website" }),
      setOrCreateMeta('meta[property="og:title"]', { property: "og:title", content: OG_TITLE }),
      setOrCreateMeta('meta[property="og:description"]', { property: "og:description", content: OG_DESCRIPTION }),
      setOrCreateMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl }),
      setOrCreateMeta('meta[property="og:site_name"]', { property: "og:site_name", content: brand.brandName }),
      setOrCreateMeta('meta[property="og:image"]', { property: "og:image", content: sharedImage }),
      setOrCreateMeta('meta[property="og:image:alt"]', {
        property: "og:image:alt",
        content: `${brand.brandName} mascots supporting a California transparency petition`,
      }),
      setOrCreateMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
      setOrCreateMeta('meta[name="twitter:title"]', { name: "twitter:title", content: OG_TITLE }),
      setOrCreateMeta('meta[name="twitter:description"]', { name: "twitter:description", content: OG_DESCRIPTION }),
      setOrCreateMeta('meta[name="twitter:image"]', { name: "twitter:image", content: sharedImage }),
      setOrCreateMeta('meta[name="application-name"]', { name: "application-name", content: brand.brandName }),
      setOrCreateLink('link[rel="canonical"]', { rel: "canonical", href: pageUrl }),
    ];

    return () => {
      document.title = previousTitle;
      cleanups.reverse().forEach((cleanup) => cleanup());
    };
  }, [brand, pageTitle, pageUrl, sharedImage]);

  const handleReadPetition = () => {
    petitionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setClipboardStatus("Page link copied.");
    } catch (error) {
      setClipboardStatus("Copy your browser URL manually.");
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: pageTitle,
          text: OG_TITLE,
          url: pageUrl,
        });
        setClipboardStatus("Share sheet opened.");
        return;
      }
    } catch (error) {
      // ignored on purpose
    }

    handleCopyLink();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormStatus("TODO: connect this petition interest form to a backend endpoint before accepting submissions.");
  };

  return (
    <>
      <Header />
      <main className="petitionPage">
        <section className="petitionHero" aria-labelledby="petition-hero-title">
          <div className="petitionHeroInner">
            <div className="petitionHeroCopy">
              <div className="petitionEyebrow">California civic petition draft</div>
              <h1 id="petition-hero-title" className="petitionHeroTitle">
                If it raises revenue, Californians should get to vote.
              </h1>
              <p className="petitionHeroSubheadline">
                <BrandMark /> is presenting a public transparency petition draft for California taxpayers who want
                clearer disclosure, public cost studies, and voter approval before governments increase revenue
                through fees, rates, cost recoveries, assessments, surcharges, or taxes by another name.
              </p>
              <p className="petitionHeroBody">
                This page is designed to support civic education and organizing. It does not claim that fees are
                automatically illegal. It asks for a more transparent process when a charge functions like a tax.
              </p>

              <div className="petitionHeroActions">
                <button type="button" className="petitionButton" onClick={handleReadPetition}>
                  Read the petition
                </button>
                <button type="button" className="petitionButton petitionButton--secondary" onClick={handleCopyLink}>
                  Copy page link
                </button>
                <button type="button" className="petitionButton petitionButton--ghost" onClick={handleShare}>
                  Share this page
                </button>
              </div>

              <p className="petitionMicrocopy">
                This petition is a civic advocacy draft and is not legal advice.
              </p>
            </div>

            <aside className="petitionHeroPanel" aria-label="Transparency summary">
              <div className="petitionHeroPanelHeader">What the draft asks for</div>
              <ul className="petitionSummaryList">
                <li>Voter approval before government revenue increases are imposed.</li>
                <li>Public cost studies that explain why a charge is being added.</li>
                <li>Plain-language disclosure instead of labels that hide tax-like charges.</li>
              </ul>
              <div className="petitionCallout">
                <strong>Support the principle:</strong>
                <span> If a charge changes public revenue, the public should see the full case first.</span>
              </div>
            </aside>
          </div>
        </section>

        <section className="petitionNoticeSection">
          <div className="petitionSectionInner petitionNoticeCard">
            <p>
              <strong>Disclaimer:</strong> This petition is a civic advocacy draft and is not legal advice. A formal
              California statewide initiative must be reviewed and submitted through the official California
              initiative process before signatures can be circulated for ballot qualification.
            </p>
          </div>
        </section>

        <section className="petitionSection">
          <div className="petitionSectionInner">
            <p className="petitionSlogan">No hidden taxes. No surprise fees. Plain language. Public review.</p>
          </div>
        </section>

        <section className="petitionSection petitionSection--alt">
          <div className="petitionSectionInner">
            <div className="petitionSectionHeadingBlock">
              <span className="petitionSectionLabel">The problem</span>
              <h2 className="petitionSectionTitle">When a charge behaves like a tax, people should be told clearly.</h2>
              <p className="petitionSectionIntro">
                Californians regularly see increases described as fees, rates, cost recoveries, assessments, or
                surcharges. This petition draft argues that when the practical effect is to raise public revenue,
                the public should receive a straightforward explanation before the charge is adopted.
              </p>
            </div>

            <div className="petitionProblemGrid">
              <article className="petitionCard">
                <h3>Hidden in wording</h3>
                <p>
                  Vague terminology can make a new charge sound routine even when it materially increases what people
                  pay.
                </p>
              </article>
              <article className="petitionCard">
                <h3>No plain-language notice</h3>
                <p>
                  The public should be able to read the reason, amount, and long-term effect without digging through
                  technical language.
                </p>
              </article>
              <article className="petitionCard">
                <h3>No public cost study</h3>
                <p>
                  A new revenue increase should be backed by a public explanation of costs, assumptions, and who will
                  pay.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="petitionSection">
          <div className="petitionSectionInner">
            <div className="petitionSectionHeadingBlock">
              <span className="petitionSectionLabel">Core principles</span>
              <h2 className="petitionSectionTitle">Three principles for California transparency</h2>
            </div>

            <div className="petitionPrinciplesGrid">
              <article className="petitionPrincipleCard">
                <div className="petitionPrincipleNumber">1</div>
                <h3>Voter approval</h3>
                <p>
                  Before a new charge functions like a tax increase, the public should have a direct vote or another
                  process that is equally transparent and accountable.
                </p>
              </article>
              <article className="petitionPrincipleCard">
                <div className="petitionPrincipleNumber">2</div>
                <h3>Public cost studies</h3>
                <p>
                  Officials should disclose the actual cost basis, projected revenue, and the reason a charge is being
                  raised.
                </p>
              </article>
              <article className="petitionPrincipleCard">
                <div className="petitionPrincipleNumber">3</div>
                <h3>Plain-language disclosure</h3>
                <p>
                  The label should match the substance so Californians can see whether a fee, assessment, or surcharge
                  is functioning like a tax.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="petitionSection petitionSection--alt" ref={petitionRef}>
          <div className="petitionSectionInner">
            <div className="petitionSectionHeadingBlock">
              <span className="petitionSectionLabel">Draft petition language</span>
              <h2 className="petitionSectionTitle">Support a rule that keeps revenue increases in the open.</h2>
            </div>

            <div className="petitionDraftCard">
              <p>
                This draft petition asks California to require plain-language disclosure, public cost studies, and
                voter approval before governments increase revenue through fees, rates, cost recoveries, assessments,
                surcharges, or taxes by another name.
              </p>
              <ol className="petitionRules">
                <li>Any revenue-raising charge should be described in plain language.</li>
                <li>A public cost study should explain the basis and expected impact.</li>
                <li>Charges that function like taxes should not be adopted without a clear public approval process.</li>
                <li>Revenue increases should not be hidden behind technical labels that obscure their effect.</li>
              </ol>
              <p>
                The petition is meant to spark public review and civic discussion, not to claim immediate legal force.
              </p>
            </div>
          </div>
        </section>

        <section className="petitionSection">
          <div className="petitionSectionInner petitionCtaGrid">
            <div>
              <span className="petitionSectionLabel">Take action</span>
              <h2 className="petitionSectionTitle">Share the page, copy the link, and keep the conversation moving.</h2>
              <p className="petitionSectionIntro">
                If you want to help bring more transparency to government revenue increases in California, use the
                buttons below to share this draft with your network.
              </p>
              <p className="petitionInlineStatus" aria-live="polite">
                {clipboardStatus || ""}
              </p>
            </div>
            <div className="petitionActionStack">
              <button type="button" className="petitionButton" onClick={handleShare}>
                Share petition page
              </button>
              <button type="button" className="petitionButton petitionButton--secondary" onClick={handleCopyLink}>
                Copy petition link
              </button>
            </div>
          </div>
        </section>

        <section className="petitionSection petitionSection--alt">
          <div className="petitionSectionInner">
            <div className="petitionSectionHeadingBlock">
              <span className="petitionSectionLabel">Stay informed</span>
              <h2 className="petitionSectionTitle">Interest list form</h2>
              <p className="petitionSectionIntro">
                This front-end form is a placeholder only. There is no backend intake endpoint connected yet.
              </p>
            </div>

            <form className="petitionForm" onSubmit={handleSubmit}>
              <div className="petitionFieldGrid">
                <label className="petitionField">
                  <span className="petitionLabel">Name</span>
                  <input className="petitionInput" type="text" name="name" placeholder="Your name" />
                </label>
                <label className="petitionField">
                  <span className="petitionLabel">Email</span>
                  <input className="petitionInput" type="email" name="email" placeholder="you@example.com" />
                </label>
                <label className="petitionField">
                  <span className="petitionLabel">City</span>
                  <input className="petitionInput" type="text" name="city" placeholder="Los Angeles" />
                </label>
                <label className="petitionField">
                  <span className="petitionLabel">County</span>
                  <input className="petitionInput" type="text" name="county" placeholder="Los Angeles County" />
                </label>
              </div>

              <label className="petitionField petitionField--fullWidth">
                <span className="petitionLabel">Optional note</span>
                <textarea
                  className="petitionTextarea"
                  name="message"
                  rows="4"
                  placeholder="Tell us why this transparency petition matters to you."
                />
              </label>

              <div className="petitionFormActions">
                <button type="submit" className="petitionButton">
                  Placeholder submit
                </button>
                <p className="petitionFormNote">TODO: wire this form to a campaign intake endpoint before accepting entries.</p>
              </div>

              {formStatus ? <p className="petitionStatusMessage">{formStatus}</p> : null}
            </form>
          </div>
        </section>

        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </main>
      <Footer />
    </>
  );
}
